import { DialogComponent } from '@theme/dialog';
import { CartAddEvent, ThemeEvents } from '@theme/events';
import { syncUgCartShippingProgress } from '@theme/ug-cart-drawer-ui';

/**
 * 复制文本到剪贴板。
 * navigator.clipboard 只在安全上下文可用，HTTP 预览域要退回 execCommand；
 * 而 execCommand 在 iOS 上经常静默失败，必须看返回值。
 * @param {string} text
 * @returns {Promise<boolean>} 是否真的复制成功
 */
async function copyToClipboard(text) {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (error) {
      // 权限被拒或非安全上下文，继续走下面的兜底
    }
  }

  const textArea = document.createElement('textarea');
  textArea.value = text;
  textArea.setAttribute('readonly', '');
  // iOS 要求元素在视口内且可选中，只能用透明 1px，不能 display:none 或挪到屏幕外
  textArea.style.cssText = 'position:fixed;top:0;left:0;width:1px;height:1px;opacity:0;border:0;padding:0;';
  document.body.appendChild(textArea);

  let copied = false;
  try {
    textArea.select();
    textArea.setSelectionRange(0, text.length);
    copied = document.execCommand('copy');
  } catch (error) {
    copied = false;
  }
  textArea.remove();

  return copied;
}

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {ResizeObserver | undefined} */
  #recommendationNavResizeObserver;

  /**
   * 已接入 ResizeObserver 的横滑容器。
   * 对已在观察中的元素再 observe()，按规范等同于先 unobserve 再重新加入，
   * 基准尺寸被重置成 (0, 0)，下一轮立刻判定为变化 —— 抽屉打开期间就会每帧自激循环。
   * @type {WeakSet<Element>}
   */
  #observedRecommendationLists = new WeakSet();

  /** @type {number | undefined} */
  #recommendationNavFrame;

  /** 分期付款 CTA（在别人的影子树里），断开时要自己摘掉监听 @type {Element | null} */
  #installmentsCta = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener('click', this.#handleRecommendationCodeCopy);
    document.addEventListener('click', this.#handleRecommendationNavClick);
    document.addEventListener('scroll', this.#handleRecommendationListScroll, true);
    document.addEventListener(CartAddEvent.eventName, this.#scheduleRecommendationNavUpdate);
    // 自动带码成功走 discount:update morph，需重新绑定推荐区导航
    document.addEventListener(ThemeEvents.discountUpdate, this.#scheduleRecommendationNavUpdate);
    window.addEventListener('resize', this.#scheduleRecommendationNavUpdate);

    if ('ResizeObserver' in window) {
      // 回调只读、不碰观察目标：直接接 #scheduleRecommendationNavUpdate 会重新 observe 已观察元素，
      // 触发上面说的自激循环
      this.#recommendationNavResizeObserver = new ResizeObserver(this.#requestRecommendationNavUpdate);
    }

    this.#bindInstallmentsCta();
    this.#scheduleRecommendationNavUpdate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener('click', this.#handleRecommendationCodeCopy);
    document.removeEventListener('click', this.#handleRecommendationNavClick);
    document.removeEventListener('scroll', this.#handleRecommendationListScroll, true);
    document.removeEventListener(CartAddEvent.eventName, this.#scheduleRecommendationNavUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.#scheduleRecommendationNavUpdate);
    window.removeEventListener('resize', this.#scheduleRecommendationNavUpdate);
    this.#recommendationNavResizeObserver?.disconnect();

    if (this.#recommendationNavFrame) {
      cancelAnimationFrame(this.#recommendationNavFrame);
      this.#recommendationNavFrame = undefined;
    }

    this.#installmentsCta?.removeEventListener('click', this.closeDialog);
    this.#installmentsCta = null;
  }

  /**
   * 点分期付款 CTA 会打开另一个 dialog，要先把抽屉关掉，避免两层 dialog 叠在一起。
   * 只在这里绑一次：放在 open() 里会每次开抽屉都往影子树追加一个监听，
   * 而 { once: true } 要等被点过之后才移除，反复开关就会累积。
   */
  #bindInstallmentsCta() {
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      if (!cta) return;

      this.#installmentsCta = cta;
      cta.addEventListener('click', this.closeDialog);
    });
  }

  #handleCartAdd = () => {
    // 自动带码进行中：等折扣就绪后再由 ug-auto-discount 打开抽屉
    if (window.UGAutoDiscountBridge?.holdDrawer) return;
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  showDialog() {
    super.showDialog();
    this.#scheduleRecommendationNavUpdate();
    syncUgCartShippingProgress();
  }

  #scheduleRecommendationNavUpdate = () => {
    this.#observeRecommendationLists();
    this.#requestRecommendationNavUpdate();
    // 抽屉刚打开/刚 morph 时尺寸还没稳，补两次延迟重算
    window.setTimeout(this.#requestRecommendationNavUpdate, 80);
    window.setTimeout(this.#requestRecommendationNavUpdate, 260);
  };

  /**
   * 把一帧内的多次请求合并成一次。
   * 每次重算都要读 scrollWidth/clientWidth 强制同步布局，中低端 Android 上很吃帧。
   */
  #requestRecommendationNavUpdate = () => {
    if (this.#recommendationNavFrame) return;

    this.#recommendationNavFrame = requestAnimationFrame(() => {
      this.#recommendationNavFrame = undefined;
      this.#updateAllRecommendationNavs();
    });
  };

  /** 只对尚未观察的新元素 observe()，重复 observe 会重置基准尺寸并引发自激循环 */
  #observeRecommendationLists() {
    const observer = this.#recommendationNavResizeObserver;
    if (!observer) return;

    this.querySelectorAll('.cart-drawer__recommendations-list').forEach((list) => {
      if (this.#observedRecommendationLists.has(list)) return;

      this.#observedRecommendationLists.add(list);
      observer.observe(list);
    });
  }

  #updateAllRecommendationNavs = () => {
    this.querySelectorAll('.cart-drawer__recommendations').forEach((recommendations) => {
      if (recommendations instanceof HTMLElement) {
        this.#updateRecommendationNav(recommendations);
      }
    });
  };

  /**
   * @param {HTMLElement} recommendations
   */
  #updateRecommendationNav(recommendations) {
    const list = recommendations.querySelector('.cart-drawer__recommendations-list');
    const prevButton = recommendations.querySelector('[data-cart-drawer-recommendations-prev]');
    const nextButton = recommendations.querySelector('[data-cart-drawer-recommendations-next]');

    if (!(list instanceof HTMLElement) || !(prevButton instanceof HTMLButtonElement) || !(nextButton instanceof HTMLButtonElement)) return;

    const maxScrollLeft = list.scrollWidth - list.clientWidth;
    const currentScrollLeft = list.scrollLeft;
    const itemCount =
      Number(recommendations.dataset.cartDrawerRecommendationsCount) ||
      list.querySelectorAll('.cart-drawer__recommendation-card').length;
    const isMeasurable = list.clientWidth > 0 && list.scrollWidth > 0;
    const canScroll = maxScrollLeft > 1;

    prevButton.disabled = currentScrollLeft <= 1;
    nextButton.disabled = isMeasurable ? !canScroll || currentScrollLeft >= maxScrollLeft - 1 : itemCount <= 2;
  }

  /**
   * @param {HTMLButtonElement} button
   * @param {number} direction
   */
  #scrollRecommendations(button, direction) {
    const recommendations = button.closest('.cart-drawer__recommendations');
    const list = recommendations?.querySelector('.cart-drawer__recommendations-list');
    const firstCard = list?.querySelector('.cart-drawer__recommendation-card');

    if (!(recommendations instanceof HTMLElement) || !(list instanceof HTMLElement) || !(firstCard instanceof HTMLElement)) return;

    const listStyle = window.getComputedStyle(list);
    const gap = Number.parseFloat(listStyle.columnGap || listStyle.gap) || 0;
    const scrollAmount = firstCard.getBoundingClientRect().width + gap;

    list.scrollBy({
      left: direction * scrollAmount,
      behavior: 'smooth',
    });

    window.setTimeout(() => this.#updateRecommendationNav(recommendations), 280);
  }

  /**
   * @param {MouseEvent} event
   */
  #handleRecommendationNavClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const prevButton = target.closest('[data-cart-drawer-recommendations-prev]');
    const nextButton = target.closest('[data-cart-drawer-recommendations-next]');

    if (prevButton instanceof HTMLButtonElement) {
      this.#scrollRecommendations(prevButton, -1);
    } else if (nextButton instanceof HTMLButtonElement) {
      this.#scrollRecommendations(nextButton, 1);
    }
  };

  /**
   * @param {Event} event
   */
  #handleRecommendationListScroll = (event) => {
    const list = event.target;

    if (!(list instanceof HTMLElement) || !list.classList.contains('cart-drawer__recommendations-list')) return;

    const recommendations = list.closest('.cart-drawer__recommendations');

    if (recommendations instanceof HTMLElement) {
      this.#updateRecommendationNav(recommendations);
    }
  };

  /**
   * Handles copy-code clicks for dynamically refreshed cart drawer recommendations.
   * @param {MouseEvent} event
   */
  #handleRecommendationCodeCopy = async (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest('[data-cart-drawer-copy-code]');
    if (!(button instanceof HTMLButtonElement)) return;

    event.preventDefault();

    const code = button.getAttribute('data-cart-drawer-copy-code');
    const copyText = button.getAttribute('data-copy-text') || 'Copy';
    const copiedText = button.getAttribute('data-copied-text') || 'Copied!';
    const copyFailedText = button.getAttribute('data-copy-failed-text') || 'Long-press the code to copy';

    if (!code) return;

    // 谎报「已复制」比复制不上更糟：用户会直接去结算，发现没折扣
    if (!(await copyToClipboard(code))) {
      console.warn('[cart-drawer] 折扣码复制失败', code);
      // 非安全上下文或权限被拒时，只 console.warn 的话用户反复点也没任何反应。
      // 换成「长按复制」提示，并让旁边的码文本完整可选中，用户至少能手动复制。
      button.textContent = copyFailedText;
      button.classList.add('is-copy-failed');
      this.#revealDiscountCodeText(button);
      return;
    }

    button.textContent = copiedText;
    button.classList.add('is-copied');

    window.setTimeout(() => {
      button.textContent = copyText;
      button.classList.remove('is-copied');
    }, 1600);
  };

  /**
   * 复制失败兜底：码文本默认是 nowrap + 省略号，这里恢复成完整显示且可长按选中。
   * @param {HTMLButtonElement} button
   */
  #revealDiscountCodeText(button) {
    const codeText = button.parentElement?.querySelector('p');
    if (!(codeText instanceof HTMLElement)) return;

    codeText.style.overflow = 'visible';
    codeText.style.whiteSpace = 'normal';
    codeText.style.userSelect = 'text';
    codeText.style.webkitUserSelect = 'text';
  }

  open() {
    this.showDialog();
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
